import { getDefinition, validateOptions } from "./registry.js";
import { themeFor } from "./themes.js";
import {
  E,
  short,
  text,
  rect,
  path,
  missing,
  reading,
  part,
} from "./render-kit.js";
import {
  instrumentRendererIds,
  renderInstrument,
} from "./render-instruments.js";
import { contextRendererIds, renderContext } from "./render-context.js";
import { systemRendererIds, renderSystem } from "./render-systems.js";
import { withPartOrigins } from "./part-origins.js";
import { FACE_WITHOUT_READING } from "./fit.js";
import { marinaKinds } from "./marina-theme.generated.js";
import { pageDomainFor } from "./templates/domains.js";

// A face takes its reading's kind's colour as its accent (cloud ADR 0396,
// TOM-678; VENDORED.md change 15) when its host asks (`presentation.kindAccent`:
// the page drawer does for a face whose look names no accent, so an accent
// the owner chose is kept): the dials, gauges and graphs, and the number
// faces, whose accent marks the reading (a needle, a fill, a line) and never
// colours its text. A face of no kind, or of another family, keeps the coral.
const KIND_FAMILIES = new Set(["angular", "gauge", "graph"]);
const KIND_READOUTS = new Set(["number", "trend-number"]);

/** The kind colour a face takes as its accent, or null. */
function kindAccent(instance, definition, theme) {
  if (
    !KIND_FAMILIES.has(definition.family) &&
    !KIND_READOUTS.has(definition.id)
  )
    return null;
  const bindings = instance.bindings ?? {};
  const roles = [
    ...Object.keys(definition.roles ?? {}),
    ...Object.keys(bindings),
  ];
  for (const role of roles) {
    const bound = bindings[role];
    const path = typeof bound === "string" ? bound : bound?.sourceId;
    if (typeof path !== "string") continue;
    const kind = pageDomainFor(path);
    return kind === null ? null : (marinaKinds[theme]?.[kind]?.line ?? null);
  }
  return null;
}

export { E as escapeXML } from "./render-kit.js";
export const implementedRendererIds = [
  ...instrumentRendererIds,
  ...contextRendererIds,
  ...systemRendererIds,
];
const renderers = new Map([
  ...instrumentRendererIds.map((id) => [id, renderInstrument]),
  ...contextRendererIds.map((id) => [id, renderContext]),
  ...systemRendererIds.map((id) => [id, renderSystem]),
]);
function headerIcon(family) {
  const icons = {
    angular: "M20 15L26 27L20 24L14 27Z",
    readout: "M15 16H25V26H15Z M18 19H23 M18 23H21",
    gauge: "M14 25A7 7 0 1 1 26 25 M20 23L24 18",
    graph: "M14 26V15 M14 26H27 M16 23L20 19L24 21L27 16",
    navigation: "M14 20H24Q29 20 27 16 M14 24H23Q27 24 25 27",
    command: "M20 14V21 M16 16A7 7 0 1 0 24 16",
    media: "M14 16H26V26H14Z M16 23L20 19L24 23",
    composite: "M14 17H27V25H14Z M17 20V23 M21 20V23 M25 20V23",
  };
  return path(
    icons[family] ?? icons.readout,
    "var(--accent)",
    1.25,
    part("icon"),
  );
}
export function safeMediaURL(url) {
  try {
    const u = new URL(
      url,
      typeof location !== "undefined" ? location.href : "https://localhost/",
    );
    return ["https:", "http:", "file:", "blob:"].includes(u.protocol)
      ? u.href
      : null;
  } catch {
    return null;
  }
}
export function renderControl(instance, data = {}, presentation = {}) {
  const definition = getDefinition(instance.definitionId);
  const o = validateOptions(instance.definitionId, instance.options, true);
  const t = themeFor(o);
  const kind =
    presentation.kindAccent === true
      ? kindAccent(instance, definition, o.theme ?? "dark")
      : null;
  if (kind !== null) t.accent = kind;
  const samples = Object.values(data.values ?? data.service?.values ?? {});
  const quality =
    data.quality ??
    (samples.some((s) => ["missing", "disconnected"].includes(s?.quality))
      ? "missing"
      : samples.some((s) => s?.quality === "stale")
        ? "stale"
        : samples.length
          ? "good"
          : ["note", "divider"].includes(definition.id)
            ? "good"
            : "missing");
  const uid = `tc-${String(instance.id ?? definition.id).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const body =
    ["missing", "disconnected"].includes(quality) &&
    !["note", "divider"].includes(definition.id) &&
    !FACE_WITHOUT_READING.has(definition.id)
      ? missing(
          quality === "disconnected"
            ? "Source disconnected"
            : "Source unavailable",
        )
      : renderers.get(definition.id)(
          definition.id,
          data,
          {
            ...o,
            _svgId: uid,
            _motionPhase: presentation.motionPhase ?? "settled",
          },
          {
            renderControl: (child, childData) =>
              renderControl(child, childData, presentation),
          },
        );
  if (typeof body !== "string")
    throw new Error(`Renderer did not return a face: ${definition.id}`);
  const first = samples[0];
  const age =
    first?.measuredAt !== undefined && data.now !== undefined
      ? Math.max(0, Math.floor((data.now - first.measuredAt) / 1000))
      : null;
  let footer = o.showSource
    ? short(
        data.sourceLabel ??
          first?.sourceLabel ??
          data.service?.source ??
          "Source unavailable",
        36,
      )
    : "";
  if ((o.showAge || quality === "stale") && age !== null)
    footer += `${footer ? " · " : ""}${age}s old`;
  const status = [
    data.illustrative ? "ILLUSTRATIVE" : "",
    quality === "good" ? "" : quality.toUpperCase(),
  ]
    .filter(Boolean)
    .join(" · ");
  const assetLayer =
    ["diagram", "tank"].includes(definition.id) &&
    data.media?.approved &&
    safeMediaURL(data.media.url);
  const vars = Object.entries(t)
    .map(([k, v]) => `--${k}:${v}`)
    .join(";");
  const title =
    o.showTitle && o.captionPosition !== "below"
      ? headerIcon(definition.family) +
        text(
          36,
          30,
          short(o.title, 36),
          13,
          "var(--accent)",
          "start",
          part("title", 'font-weight="550"'),
        )
      : "";
  const subtitle = o.subtitle || (o.captionPosition === "below" ? o.title : "");
  // Every named part turns in place under a page's plain transform (TOM-587,
  // part-origins.js).
  const svg = withPartOrigins(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" role="img" aria-label="${E(o.title)}${status ? ` — ${E(status)}` : ""}" style="${vars};font-family:${o.font === "mono" ? "var(--tc-font-mono,TCMono,monospace)" : "var(--tc-font-sans,TCInter,sans-serif)"};font-variant-numeric:tabular-nums${assetLayer ? ";position:relative;pointer-events:none" : ""}" data-renderer="${E(definition.id)}" data-design-pass="${o.variant}"><defs><pattern id="${uid}-unknownHatch" width="6" height="6" patternUnits="userSpaceOnUse"><path d="M0 6 L6 0" stroke="var(--muted)" stroke-width="1"/></pattern></defs>${rect(0, 0, 400, 300, assetLayer || o.surface === "transparent" || o.background === "transparent" ? "transparent" : "var(--background)", 24, part("background", 'data-background="true"'))}${title}<g class="control-face">${body}</g>${subtitle ? text(24, 267, short(subtitle, 54), 10, "var(--muted)", "start", part("subtitle")) : ""}${text(24, 286, footer, 8, "var(--quiet)", "start", part("footer"))}${text(376, 286, status, 8, quality === "stale" ? "var(--muted)" : "var(--quiet)", "end", part("status"))}</svg>`,
  );
  const roles = Object.keys(data.values ?? data.service?.values ?? {}).slice(
    0,
    8,
  );
  const summary = roles
    .map((role) => {
      const r = reading(data, role, o);
      return `${role}: ${r.value} ${r.unit}`;
    })
    .join(". ");
  return {
    svg,
    quality,
    options: o,
    definitionId: definition.id,
    designSet: o.variant,
    accessibleSummary: `${o.title}. ${summary}${summary ? ". " : ""}${quality}.`,
    data,
  };
}
